import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBell } from "react-icons/fa";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import {
  APS_REVIEW_OPEN_EVENT,
  APS_REVIEW_READY_EVENT,
  APS_REVIEW_SEEN_EVENT,
} from "../utils/apsReviewEvents";
import ApsReviewModal from "./ApsReviewModal";

const POLL_MS = 8000;

function isMissingDocsAlert(alert) {
  return (
    alert?.kind === "trade-missing-docs" ||
    alert?.kind === "listing-missing-docs"
  );
}

function isOfficeMissingDocsAlert(alert) {
  return isMissingDocsAlert(alert) && alert?.audience === "office";
}

function isAgentMissingDocsAlert(alert) {
  return isMissingDocsAlert(alert) && alert?.audience !== "office";
}

function isTrsSignedAlert(alert) {
  return alert?.kind === "trade-trs-signed";
}

function formatAlertWhen(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return "";
  }
}

function uploadAlertTitle(alert) {
  if (isMissingDocsAlert(alert)) {
    const label = alert.recordLabel || "Missing documents";
    return isOfficeMissingDocsAlert(alert)
      ? `Missing documents requested — ${label}`
      : label;
  }
  const place = alert.place ? ` — ${alert.place}` : "";
  return `${alert.recordLabel || "Trade"}${place}`;
}

function missingDocsMessage(alert) {
  const who = alert?.uploadedBy || "Admin";
  const when = formatAlertWhen(alert?.createdAt);
  const names = (alert?.fileNames || []).filter(Boolean);
  const docs = names.length
    ? names.join(", ")
    : alert?.otherMessage
      ? "Other (see note)"
      : "";

  if (isOfficeMissingDocsAlert(alert)) {
    const parts = [`${who} requested missing documents`];
    if (when) parts.push(`on ${when}`);
    if (docs) parts.push(`— ${docs}`);
    return parts.join(" ");
  }

  if (docs) {
    return when
      ? `Please upload: ${docs} (requested ${when}).`
      : `Please upload: ${docs}.`;
  }
  return when
    ? `Please upload the requested missing documents (requested ${when}).`
    : "Please upload the requested missing documents.";
}

function uploadAlertBody(alert) {
  if (isMissingDocsAlert(alert)) {
    return missingDocsMessage(alert);
  }
  if (isTrsSignedAlert(alert)) {
    return (
      alert.summary ||
      `${alert.itemLabel || "TRS"} was signed. Open Trade Details to download it.`
    );
  }
  const who = alert.uploadedBy || "An agent";
  const names = (alert.fileNames || []).filter(Boolean);
  const typeLabel = String(alert.itemLabel || "").replace(/\.$/, "");
  const docs = typeLabel
    ? names.length
      ? `${typeLabel} (${names.join(", ")})`
      : typeLabel
    : names.join(", ") || "documents";
  return `${who} uploaded these documents: ${docs}`;
}

function uploadAlertToast(alert) {
  if (isMissingDocsAlert(alert)) {
    return missingDocsMessage(alert);
  }
  if (isTrsSignedAlert(alert)) {
    return (
      alert.summary ||
      `${alert.itemLabel || "TRS"} was signed for ${alert.recordLabel || "this trade"}.`
    );
  }
  if (alert.summary) return alert.summary;
  const who = alert.uploadedBy || "An agent";
  const names = (alert.fileNames || []).filter(Boolean);
  const typeLabel = String(alert.itemLabel || "").replace(/\.$/, "");
  const docs = typeLabel || names.join(", ") || "documents";
  const trade = alert.recordLabel || "this trade";
  const place = alert.place ? ` (${alert.place})` : "";
  return `${who} uploaded these documents under ${trade}${place}: ${docs}`;
}

const ApsReviewNotifications = () => {
  const navigate = useNavigate();
  const { isAgent, isSuperUser, isAdmin, isBroker } = useRole();
  const canClearAlerts = isSuperUser || isAdmin || isBroker;
  const [items, setItems] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [clearingId, setClearingId] = useState("");
  const menuRef = useRef(null);
  const knownUploadIdsRef = useRef(null);
  const knownReviewKeysRef = useRef(null);

  const reviewKey = (row) =>
    row.kind === "listing" || row.listingId
      ? `listing:${row.listingId}`
      : `trade:${row.tradeId}`;

  const loadUnseen = async () => {
    const nextItems = [];
    if (!isAgent) {
      try {
        const apsRes = await axiosInstance.get("/trade-documents/aps-reviews", {
          params: { unseen: 1 },
        });
        nextItems.push(
          ...(apsRes.data.reviews || []).map((row) => ({
            ...row,
            kind: "trade",
          }))
        );
      } catch (_) {}
      try {
        const listingRes = await axiosInstance.get(
          "/listing-documents/document-reviews",
          { params: { unseen: 1 } }
        );
        nextItems.push(
          ...(listingRes.data.reviews || []).map((row) => ({
            ...row,
            kind: "listing",
          }))
        );
      } catch (_) {}
    }
    if (knownReviewKeysRef.current) {
      const known = knownReviewKeysRef.current;
      const fresh = nextItems.filter(
        (row) => row.hasReport && !known.has(reviewKey(row)),
      );
      fresh.forEach((row) => {
        const label =
          row.kind === "listing"
            ? `Listing #${row.listingNumber || ""}`
            : `Trade #${row.tradeNumber || ""}`;
        const emails = (row.agentEmailedTo || []).filter(Boolean);
        if (row.emailedToAgent) {
          toast.success(
            emails.length
              ? `Compliance review was sent to ${emails.join(", ")}.`
              : `Compliance review was sent to the agent for ${label}.`,
            { autoClose: 10000 },
          );
        }
        toast.info(
          `Compliance Review is ready for ${label}. Click to view.`,
          {
            autoClose: false,
            closeOnClick: true,
            onClick: () => openReport(row),
          },
        );
      });
    }
    knownReviewKeysRef.current = new Set(nextItems.map(reviewKey));
    setItems(nextItems);
    try {
      const uploadRes = await axiosInstance.get("/office-alerts", {
        params: { unseen: 1 },
      });
      const nextUploads = uploadRes.data.alerts || [];
      if (knownUploadIdsRef.current) {
        const known = knownUploadIdsRef.current;
        const fresh = nextUploads.filter((row) => !known.has(String(row.id)));
        fresh.forEach((row) => {
          toast.info(uploadAlertToast(row), {
            autoClose: isAgentMissingDocsAlert(row) ? false : 12000,
          });
        });
      }
      knownUploadIdsRef.current = new Set(
        nextUploads.map((row) => String(row.id))
      );
      setUploads(nextUploads);
    } catch (_) {
      setUploads([]);
    }
  };

  const openReport = async (item) => {
    setOpen(false);
    if (item.kind === "listing" || item.listingId) {
      setActive({
        kind: "listing",
        listingId: item.listingId,
        listingNumber: item.listingNumber,
      });
      try {
        await axiosInstance.patch(
          `/listing-documents/${item.listingId}/document-review/seen`
        );
      } catch (_) {}
      setItems((prev) =>
        prev.filter((row) => row.listingId !== item.listingId)
      );
      return;
    }
    setActive({
      kind: "trade",
      tradeId: item.tradeId,
      tradeNumber: item.tradeNumber,
    });
    try {
      await axiosInstance.patch(
        `/trade-documents/${item.tradeId}/aps-review/seen`
      );
    } catch (_) {}
    setItems((prev) => prev.filter((row) => row.tradeId !== item.tradeId));
  };

  const openUpload = async (alert) => {
    setOpen(false);
    // Agent missing-docs stay until explicitly cleared; office request logs dismiss on open.
    if (!isAgentMissingDocsAlert(alert)) {
      try {
        await axiosInstance.patch(`/office-alerts/${alert.id}/seen`);
      } catch (_) {}
      setUploads((prev) => prev.filter((row) => row.id !== alert.id));
    }
    if (alert.path) navigate(alert.path);
  };

  const clearAlert = async (alert, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!canClearAlerts || !alert?.id) return;
    setClearingId(alert.id);
    try {
      await axiosInstance.patch(`/office-alerts/${alert.id}/seen`);
      setUploads((prev) => prev.filter((row) => row.id !== alert.id));
      toast.success("Notification cleared for the agent.");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not clear the notification."
      );
    } finally {
      setClearingId("");
    }
  };

  useEffect(() => {
    loadUnseen();
    const timer = window.setInterval(loadUnseen, POLL_MS);
    const onReady = (event) => {
      const detail = event.detail || {};
      if (detail.listingId) {
        if (detail.preparing) {
          toast.info(
            `Preparing the compliance review for Listing #${detail.listingNumber || ""}. You will get a notification when it is ready.`
          );
          return;
        }
        const item = {
          kind: "listing",
          listingId: String(detail.listingId),
          listingNumber: detail.listingNumber,
          summary: detail.payload?.report?.summary || "",
          overallStatus: detail.payload?.report?.overallStatus || "",
          hasReport: true,
          notificationSeen: false,
        };
        setItems((prev) => [
          item,
          ...prev.filter((row) => row.listingId !== item.listingId),
        ]);
        toast.info(
          `Compliance Review is ready for Listing #${detail.listingNumber || ""}. Click to view.`,
          {
            autoClose: false,
            closeOnClick: true,
            onClick: () => openReport(item),
          }
        );
        return;
      }
      if (!detail.tradeId) return;
      if (detail.preparing) {
        toast.info(
          `Preparing the compliance review for Trade #${detail.tradeNumber || ""}. You will get a notification when it is ready.`
        );
        return;
      }
      const item = {
        kind: "trade",
        tradeId: String(detail.tradeId),
        tradeNumber: detail.tradeNumber,
        summary: detail.payload?.report?.summary || "",
        overallStatus: detail.payload?.report?.overallStatus || "",
        hasReport: true,
        notificationSeen: false,
      };
      setItems((prev) => [
        item,
        ...prev.filter((row) => row.tradeId !== item.tradeId),
      ]);
      toast.info(
        `Compliance Review is ready for Trade #${detail.tradeNumber || ""}. Click to view.`,
        {
          autoClose: false,
          closeOnClick: true,
          onClick: () => openReport(item),
        }
      );
    };
    const onOpen = (event) => {
      const detail = event.detail || {};
      if (detail.listingId || detail.tradeId) openReport(detail);
    };
    const onSeen = (event) => {
      const tradeId = String(event.detail?.tradeId || "");
      const listingId = String(event.detail?.listingId || "");
      if (!tradeId && !listingId) return;
      setItems((prev) =>
        prev.filter((row) => {
          if (listingId && row.listingId === listingId) return false;
          if (tradeId && row.tradeId === tradeId) return false;
          return true;
        })
      );
    };
    window.addEventListener(APS_REVIEW_READY_EVENT, onReady);
    window.addEventListener(APS_REVIEW_OPEN_EVENT, onOpen);
    window.addEventListener(APS_REVIEW_SEEN_EVENT, onSeen);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(APS_REVIEW_READY_EVENT, onReady);
      window.removeEventListener(APS_REVIEW_OPEN_EVENT, onOpen);
      window.removeEventListener(APS_REVIEW_SEEN_EVENT, onSeen);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgent]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const count = items.length + uploads.length;

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="relative flex items-center justify-center text-blue-900 bg-gray-100 p-2 rounded-md border border-gray-200 hover:bg-gray-200 transition-colors"
          title="Notifications"
        >
          <FaBell className="text-lg" />
          {count > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] leading-[18px] font-bold">
              {count}
            </span>
          )}
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-2xl p-2 z-50 max-h-[70vh] overflow-y-auto">
            {count === 0 ? (
              <p className="text-sm text-gray-500 px-3 py-4">
                No new notifications.
              </p>
            ) : (
              <>
                {uploads.map((alert) => (
                  <div
                    key={alert.id}
                    className="px-3 py-2 rounded-lg hover:bg-gray-50"
                  >
                    <button
                      type="button"
                      onClick={() => openUpload(alert)}
                      className="w-full text-left"
                    >
                      <p className="text-sm font-semibold text-gray-900">
                        {uploadAlertTitle(alert)}
                      </p>
                      <p className="text-xs text-gray-600">
                        {uploadAlertBody(alert)}
                      </p>
                      {isOfficeMissingDocsAlert(alert) ? (
                        <>
                          {formatAlertWhen(alert.createdAt) ? (
                            <p className="text-[11px] text-gray-500 mt-0.5">
                              Requested {formatAlertWhen(alert.createdAt)}
                              {alert.uploadedBy
                                ? ` by ${alert.uploadedBy}`
                                : ""}
                            </p>
                          ) : null}
                          {(alert.fileNames || []).filter(Boolean).length ? (
                            <p className="text-[11px] text-gray-700 mt-0.5">
                              Documents:{" "}
                              {(alert.fileNames || []).filter(Boolean).join(", ")}
                            </p>
                          ) : null}
                        </>
                      ) : null}
                      {alert.categoryLabel ? (
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {alert.categoryLabel}
                        </p>
                      ) : null}
                      {isAgentMissingDocsAlert(alert) && isAgent ? (
                        <p className="text-[11px] text-blue-800 mt-1">
                          This stays until a Super User clears it.
                        </p>
                      ) : null}
                    </button>
                    {isAgentMissingDocsAlert(alert) && canClearAlerts ? (
                      <button
                        type="button"
                        onClick={(event) => clearAlert(alert, event)}
                        disabled={clearingId === alert.id}
                        className="mt-2 text-xs font-medium text-blue-900 border border-blue-200 rounded px-2 py-1 hover:bg-blue-50 disabled:opacity-60"
                      >
                        {clearingId === alert.id
                          ? "Clearing…"
                          : "Clear for agent"}
                      </button>
                    ) : null}
                  </div>
                ))}
                {items.map((item) => (
                  <button
                    key={item.listingId || item.tradeId}
                    type="button"
                    onClick={() => openReport(item)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50"
                  >
                    <p className="text-sm font-semibold text-gray-900">
                      {item.listingId
                        ? `Listing #${item.listingNumber}`
                        : `Trade #${item.tradeNumber}`}
                    </p>
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {item.emailedToAgent
                        ? `Review was sent to ${(item.agentEmailedTo || [])
                            .filter(Boolean)
                            .join(", ") || "the agent"}. `
                        : ""}
                      {item.summary ||
                        "Compliance Review is ready. Click to view."}
                    </p>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>
      {active ? (
        <ApsReviewModal
          tradeId={active.tradeId}
          tradeNumber={active.tradeNumber}
          listingId={active.listingId}
          listingNumber={active.listingNumber}
          onClose={() => setActive(null)}
        />
      ) : null}
    </>
  );
};

export default ApsReviewNotifications;
